import { useId, useMemo } from 'react'

import { Body1, Body2, Caption1, Title1 } from '@ui/Typography'
import { Link } from '@ui/components/Link'
import { ListItem } from '@ui/components/List'
import { PopoverList } from '@ui/components/Popover'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { ArrowCircleLeftRightIcon } from '@ui/icons/ArrowCircleLeftRight.icon'
import { CaretIcon } from '@ui/icons/Caret.icon'
import type { IconFCType } from '@ui/icons/types'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import type {
  RecentActivityView,
  WalletActionKind,
  WalletRecentActivityGroupView,
  WalletRecentActivityItemView,
} from '../wallets.types'

import styles from './RecentActivitySection.module.scss'

interface RecentActivitySectionProps {
  recentActivity: RecentActivityView
}

interface GroupedActivity {
  group: WalletRecentActivityGroupView
  items: WalletRecentActivityItemView[]
}

const transactionIcons: Record<WalletActionKind, IconFCType> = {
  deposit: ArrowCircleIcon.Down,
  withdraw: ArrowCircleIcon.UpRight,
  transfer: ArrowCircleLeftRightIcon,
}

const getGroupedActivity = (items: WalletRecentActivityItemView[]): GroupedActivity[] =>
  items.reduce<GroupedActivity[]>((groups, item) => {
    const existingGroup = groups.find((group) => group.group.id === item.group.id)

    if (existingGroup) {
      existingGroup.items.push(item)
    } else {
      groups.push({ group: item.group, items: [item] })
    }

    return groups
  }, [])

export const RecentActivitySection: React.FC<RecentActivitySectionProps> = ({ recentActivity }) => {
  const headingId = useId()
  const groupedActivity = useMemo(
    () => getGroupedActivity(recentActivity.items),
    [recentActivity.items]
  )
  const hasActivity = groupedActivity.length > 0

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Box flex align='center' justify='space-between' gap='x16'>
        <Title1 as='h2' id={headingId} medium color='text-high-em'>
          {recentActivity.title}
        </Title1>
        {hasActivity && !recentActivity.isLoading && (
          <Link
            size='md'
            variant='secondary'
            to={recentActivity.historyPath}
            state={{ walletId: recentActivity.walletId }}
          >
            {recentActivity.viewAllLabel}
          </Link>
        )}
      </Box>

      <Loading
        isLoading={!!recentActivity.isLoading}
        showLoadingIcon
        fullWidth
        wrapperClass={styles.loading}
      >
        {recentActivity.errorMessage ? (
          <Box flex direction='column' align='center' py='x32' px='x16' gap='x4' textAlign='center'>
            <Body1 medium color='text-danger-high-em'>
              {recentActivity.errorMessage}
            </Body1>
          </Box>
        ) : hasActivity ? (
          <Box flex direction='column' gap='x16' mt='x16'>
            {groupedActivity.map(({ group, items }) => (
              <Box key={group.id} flex direction='column' gap='x4'>
                <Caption1 color='text-med-em'>{group.label}</Caption1>
                <PopoverList>
                  {items.map((item) => (
                    <ListItem
                      key={item.id}
                      as='button'
                      size='md'
                      onClick={item.onSelect}
                      title={item.title}
                      subtitle={item.description}
                      startSlot={{
                        icon: { icon: transactionIcons[item.kind], color: 'text-low-em' },
                        size: 'x40',
                        shape: 'circle',
                        border: 'outline-low-em',
                      }}
                      description={{
                        title: item.amount,
                        sensitive: true,
                        whiteSpace: 'nowrap',
                      }}
                      endSlot={{
                        icon: {
                          icon: { icon: CaretIcon.Right, color: 'text-base-em' },
                          size: 'x20',
                        },
                      }}
                    />
                  ))}
                </PopoverList>
              </Box>
            ))}
          </Box>
        ) : (
          <Box flex direction='column' align='center' py='x32' px='x16' gap='x4' textAlign='center'>
            <Body1 medium color='text-high-em'>
              {recentActivity.emptyTitle}
            </Body1>
            <Body2 color='text-low-em'>{recentActivity.emptyDescription}</Body2>
          </Box>
        )}
      </Loading>
    </section>
  )
}
